<script setup lang="ts">
import type { TraderRisk, TraderRiskItem } from '~/server/detail'
import CloseBoldIcon from '~/assets/icon/close-2.svg'

const { show, risk } = defineProps<{
  show: boolean
  risk?: TraderRisk | null
}>()

const { t } = useAppI18n()

const emit = defineEmits<{
  close: []
}>()

const close = () => {
  emit('close')
}

watch(
  () => show,
  (value) => {
    if (!import.meta.client) return
    document.body.style.overflow = value ? 'hidden' : ''
  },
)

onBeforeUnmount(() => {
  if (import.meta.client) {
    document.body.style.overflow = ''
  }
})
</script>

<template>
  <Teleport to="body">
    <Transition name="risk-dialog-fade">
      <section v-if="show" class="risk-dialog-mask" @click.self="close">
        <div class="risk-dialog" role="dialog" aria-modal="true" aria-labelledby="risk-dialog-title">
          <header class="risk-dialog-header">
            <div class="risk-dialog-title-row">
              <h2 id="risk-dialog-title" class="risk-dialog-title">{{ t('83882') }}</h2>
              <button type="button" class="risk-dialog-close" aria-label="关闭风险评测弹窗" @click="close">
                <CloseBoldIcon />
              </button>
            </div>
          </header>

          <div class="risk-dialog-body">
            <div class="risk-dialog-list">
              <div
                v-for="(item, index) in risk?.items"
                :key="`${item.content}-${index}`"
                class="risk-dialog-item"
                :class="{
                  'risk-dialog-item-first': index === 0,
                  'risk-dialog-item-last': index === (risk?.items?.length || 0) - 1,
                }"
              >
                <CommonImage
                  v-if="item.level === 4"
                  src="https://resources1.interface003.com/web30/2026/06/07/a0be69ee-6f58-47f4-8a00-96a37300c0c7.png"
                  alt=""
                  class="risk-dialog-icon-image"
                />
                <CommonImage
                  v-if="item.level === 2 || item.level === 3"
                  src="https://resources1.interface003.com/web30/2026/06/07/f2116b60-df3e-4ad6-b07d-787de54c7620.png"
                  alt=""
                  class="risk-dialog-icon-image"
                />
                <p class="risk-dialog-text">{{ item.content }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Transition>
  </Teleport>
</template>

<style scoped lang="less">
.risk-dialog-mask {
  position: fixed;
  z-index: 3000;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(9, 20, 51, 0.48);
  padding: 32px;
}

.risk-dialog {
  width: min(540px, calc(100vw - 48px));
  display: flex;
  flex-direction: column;
  max-height: min(600px, calc(100vh - 64px));
  overflow: hidden;
  border-radius: 28px;
  background: #fff;
  box-shadow: 0 24px 80px rgba(8, 19, 62, 0.18);
}

.risk-dialog-header {
  background: #fff;
  padding: 32px 32px 0;
}

.risk-dialog-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 0.5px solid rgba(5, 21, 36, 0.06);
  padding-bottom: 16px;
}

.risk-dialog-title {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  margin: 0;
  color: #292a2e;
  font-family:
    PingFang SC,
    sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.risk-dialog-close {
  position: relative;
  display: flex;
  width: 32px;
  height: 32px;
  flex: 0 0 auto;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 50%;
  background: #eef0f4;
  transition:
    background 0.2s ease,
    transform 0.2s ease;
}

.risk-dialog-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background: #fff;
  padding: 20px 32px 32px;

  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.risk-dialog-list {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.risk-dialog-item {
  display: flex;
  width: 100%;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 0;
}

.risk-dialog-item-first {
  padding-top: 0;
}

.risk-dialog-item-last {
  padding-bottom: 0;
}

.risk-dialog-icon {
  display: inline-flex;
  width: 20px;
  height: 20px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
  border-radius: 50%;
  color: #fff;
  font-family: Arial, sans-serif;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
}

.risk-dialog-icon-high {
  background: #e44843;
}

.risk-dialog-icon-medium {
  background: #ff8e00;
}

.risk-dialog-icon-low {
  background: #112d73;
}

.risk-dialog-icon-image {
  width: 20px;
  height: 20px;
  object-fit: contain;
}

.risk-dialog-text {
  min-width: 0;
  flex: 1;
  margin: 0;
  color: #292a2e;
  font-family:
    PingFang SC,
    sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  word-break: break-word;
}

.risk-dialog-fade-enter-active,
.risk-dialog-fade-leave-active {
  transition: opacity 0.2s ease;
}

.risk-dialog-fade-enter-active .risk-dialog,
.risk-dialog-fade-leave-active .risk-dialog {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.risk-dialog-fade-enter-from,
.risk-dialog-fade-leave-to {
  opacity: 0;
}

.risk-dialog-fade-enter-from .risk-dialog,
.risk-dialog-fade-leave-to .risk-dialog {
  opacity: 0;
  transform: translateY(8px) scale(0.98);
}

@media screen and (max-width: 799px) {
  .risk-dialog-mask {
    padding: 16px;
  }

  .risk-dialog {
    width: 100%;
    border-radius: 20px;
  }

  .risk-dialog-header {
    padding: 24px 24px 0;
  }

  .risk-dialog-body {
    padding: 20px 24px 24px;
  }
}
</style>
